import React from "react";
import { sentenceCase } from "@/lib/sentenceCase";
import { highlightTextDiacritic } from "@/lib/textSearch";

/**
 * StructureResults — list of hierarchy hits (Chapter / Article / Parágrafo
 * / Subarticle) shown ABOVE the paragraph-body results when the user types
 * a text query. Each row is visually distinct (soft purple tint, badge
 * marking the level) so the user can tell at a glance whether a hit comes
 * from the table of contents or from the body of a numbered paragraph.
 *
 * Clicking a row invokes `onPick(start)` which the parent translates into
 * a jumpToParagraph, landing the user at the first paragraph of that
 * section — *never* a random body paragraph.
 */
export default function StructureResults({ results, query, onPick, lang }) {
    if (!results || results.length === 0) return null;
    return (
        <section
            className="mb-8"
            data-testid="ccc-structure-results"
            aria-label={lang === "es" ? "Resultados del índice" : "Index results"}
        >
            <p className="label-eyebrow mb-3">
                {lang === "es"
                    ? `Coincidencias del índice (${results.length})`
                    : `Index matches (${results.length})`}
            </p>
            <ol className="flex flex-col gap-2">
                {results.map((hit, i) => (
                    <StructureRow
                        key={`${hit.kind}-${hit.start}-${i}`}
                        hit={hit}
                        query={query}
                        onPick={onPick}
                        lang={lang}
                    />
                ))}
            </ol>
        </section>
    );
}

function StructureRow({ hit, query, onPick, lang }) {
    const badge = badgeLabel(hit.kind, lang);
    // The title already comes in mixed/proper case from the structure JSON
    // (sentenceCase is applied once on Vatican ALL-CAPS headings); we
    // re-run it here for safety and then highlight the query inside.
    const fullTitle = (hit.label ? `${hit.label}: ` : "") + sentenceCase(hit.title || "");
    return (
        <li>
            <button
                type="button"
                onClick={() => hit.start && onPick(hit.start)}
                disabled={!hit.start}
                data-testid={`ccc-structure-hit-${hit.kind}-${hit.start || "nostart"}`}
                className={[
                    "w-full text-left px-4 py-3 rounded-md transition-colors",
                    "ui-sans flex flex-col gap-1.5",
                    // Subtle purple tint — matches the accordion's open state
                    // colour but at a much lower saturation so it reads as
                    // "different from a body paragraph" without competing
                    // visually with the actual paragraph list below.
                    "bg-purple-50/50 border border-purple-200/70 hover:border-purple-400 hover:bg-purple-50",
                    !hit.start && "opacity-60 cursor-not-allowed",
                ].filter(Boolean).join(" ")}
            >
                <span className="flex items-center gap-2 flex-wrap">
                    <span
                        data-testid={`ccc-structure-hit-badge-${hit.kind}`}
                        className="ui-sans text-[10px] uppercase tracking-widest font-semibold text-purple-700 bg-purple-100/80 px-2 py-0.5 rounded"
                    >
                        {badge}
                    </span>
                    {hit.start && (
                        <span className="ui-sans text-[11px] text-stoneMuted tabular-nums">
                            §{hit.start}
                            {hit.end && hit.end !== hit.start ? `–§${hit.end}` : ""}
                        </span>
                    )}
                </span>
                <span className="reading-serif text-[15px] sm:text-base leading-snug text-stone900">
                    {highlightTextDiacritic(fullTitle, query)}
                </span>
                {hit.crumb && (
                    <span className="ui-sans text-[11px] text-stoneMuted truncate">
                        {sentenceCase(hit.crumb)}
                    </span>
                )}
            </button>
        </li>
    );
}

function badgeLabel(kind, lang) {
    if (lang === "es") {
        return {
            chapter: "Capítulo",
            article: "Artículo",
            paragrafo: "Parágrafo",
            subarticle: "Subartículo",
        }[kind] || kind;
    }
    return {
        chapter: "Chapter",
        article: "Article",
        paragrafo: "Section",
        subarticle: "Subarticle",
    }[kind] || kind;
}
